import {
  COPY_FLOOR_OVERLAY,
  DELETE_FLOOR_OVERLAY,
  EDIT_FLOOR_OVERLAY
} from "shared/context/overlay/types"
import { useEvent } from "shared/lib/useEvent"
import { useOverlay } from "shared/lib/useOverlay"
import { ProjectPermissions } from "shared/types/__generated__/graphql"

import { useDuplicateFloor } from "entities/floors/api/useDuplicateFloor"
import { useFloorFragment } from "entities/floors/model/useFloorFragment"
import { ProjectPermissionType } from "entities/projects/types/ProjectPermissions"
import { FloorFormTabType } from "features/floor-management/types/FloorFormTabType"

interface UseFloorActionHandlersArgs {
  projectID: string
  floorID: string
  permissions: ProjectPermissions
  onAction?: () => void
}

export type FilteredFloorActionHandlers = {
  onEdit: (() => void) | undefined
  onDuplicate: (() => void) | undefined
  onCopy: (() => void) | undefined
  onAssignAttachments: (() => void) | undefined
  onAddClarificationRequest: (() => void) | undefined
  onDelete: (() => void) | undefined
}

export function useFloorActionHandlers({
  projectID,
  floorID,
  permissions,
  onAction
}: UseFloorActionHandlersArgs): FilteredFloorActionHandlers {
  const floor = useFloorFragment(floorID)
  const [duplicateFloor] = useDuplicateFloor()
  const { openOverlay: editFloorOverlay } = useOverlay(EDIT_FLOOR_OVERLAY)
  const { openOverlay: openCopyFloorOverlay } = useOverlay(COPY_FLOOR_OVERLAY)
  const { openOverlay: openDeleteFloorOverlay } =
    useOverlay(DELETE_FLOOR_OVERLAY)

  const handleEditFloor = useEvent(() => {
    editFloorOverlay({ projectID, floorID })
    onAction?.()
  })

  const handleDuplicateFloor = useEvent(() => {
    if (floor) {
      // duplicateFloor already toasts, logs, and rolls back the cache; catch so
      // the re-thrown reason does not surface as an unhandled rejection.
      duplicateFloor({
        projectID,
        floorID,
        position: floor.position + 1
      }).catch(() => {})
      onAction?.()
    }
  })

  const handleCopyFloor = useEvent(() => {
    openCopyFloorOverlay({ floorID, projectID })
    onAction?.()
  })

  const handleDeleteFloor = useEvent(() => {
    openDeleteFloorOverlay({ projectID, floorID })
    onAction?.()
  })

  const handleAssignAttachments = useEvent(() => {
    editFloorOverlay({
      projectID,
      floorID,
      defaultTab: FloorFormTabType.ATTACHMENTS
    })
    onAction?.()
  })

  const handleAddClarificationRequest = useEvent(() => {
    editFloorOverlay({
      projectID,
      floorID,
      defaultTab: FloorFormTabType.CLARIFICATION_REQUEST
    })
    onAction?.()
  })

  const canEdit = permissions[ProjectPermissionType.EDIT]

  return {
    onEdit: canEdit ? handleEditFloor : undefined,
    onDuplicate: canEdit ? handleDuplicateFloor : undefined,
    onCopy: canEdit ? handleCopyFloor : undefined,
    onAssignAttachments: canEdit ? handleAssignAttachments : undefined,
    onAddClarificationRequest: canEdit
      ? handleAddClarificationRequest
      : undefined,
    onDelete: canEdit ? handleDeleteFloor : undefined
  }
}
